import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Mic, Mail, Lock, User, Building, Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', company_name: '', phone: '', agree: false
  });
  const [showPass, setShowPass] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [otp, setOtp] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const { registerStart, registerVerify, registerResend } = useAuth();

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);
  const navigate = useNavigate();

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const startCooldown = (seconds) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setResendIn(seconds);
    timerRef.current = setInterval(() => {
      setResendIn(s => {
        if (s <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const handleStart = async (e) => {
    e.preventDefault();
    if (!form.agree) return toast.error('Please agree to Terms of Service');
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');

    setLoading(true);
    try {
      const data = await registerStart(form);
      setPendingId(data.pending_id);
      startCooldown(data.resend_after || 60);
      setStep(2);
      if (data.dev_otp) toast(`Dev OTP: ${data.dev_otp}`, { duration: 10000 });
      toast.success('Verification code sent to your phone');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start registration');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerVerify(pendingId, otp);
      toast.success('Welcome! You have $5 demo credit to get started.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || loading) return;
    setLoading(true);
    try {
      const data = await registerResend(pendingId);
      startCooldown(data.resend_after || 60);
      if (data.dev_otp) toast(`Dev OTP: ${data.dev_otp}`, { duration: 10000 });
      toast.success('New code sent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--wide">
        <div className="auth-logo">
          <Mic size={32} />
          <h1>Get Started Free</h1>
          <p>$5 demo credit • No credit card required</p>
        </div>

        {step === 1 && (
          <form onSubmit={handleStart} className="auth-form">
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <div className="input-icon">
                  <User size={16} />
                  <input type="text" placeholder="John Smith" value={form.full_name}
                    onChange={e => update('full_name', e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <div className="input-icon">
                  <Building size={16} />
                  <input type="text" placeholder="My Business" value={form.company_name}
                    onChange={e => update('company_name', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Email *</label>
              <div className="input-icon">
                <Mail size={16} />
                <input type="email" placeholder="you@company.com" value={form.email}
                  onChange={e => update('email', e.target.value)} required />
              </div>
            </div>

            <div className="form-group">
              <label>Password * <small>(min 8 characters)</small></label>
              <div className="input-icon">
                <Lock size={16} />
                <input type={showPass ? 'text' : 'password'} placeholder="Choose a strong password" value={form.password}
                  onChange={e => update('password', e.target.value)} required />
                <button type="button" className="input-icon-right" onClick={() => setShowPass(s => !s)}
                  aria-label={showPass ? 'Hide password' : 'Show password'}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Phone Number *</label>
              <input type="tel" placeholder="+61 4XX XXX XXX" value={form.phone}
                onChange={e => update('phone', e.target.value)} className="input" required />
            </div>

            <div className="form-check">
              <input type="checkbox" id="agree" checked={form.agree}
                onChange={e => update('agree', e.target.checked)} />
              <label htmlFor="agree">
                I agree to the <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>
              </label>
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Sending code...' : 'Continue'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerify} className="auth-form">
            <div className="form-group">
              <label>Verification Code *</label>
              <p><small>We sent a 6-digit code to {form.phone}</small></p>
              <input type="text" inputMode="numeric" maxLength={6} placeholder="123456"
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                className="input" required autoComplete="one-time-code" autoFocus />
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading || otp.length < 6}>
              {loading ? 'Verifying...' : 'Verify & Create Account'}
            </button>

            <button type="button" className="btn btn-secondary btn-full" disabled={resendIn > 0 || loading}
              onClick={handleResend}>
              {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
            </button>
          </form>
        )}

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
